import { noop } from 'lodash';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { CaptchaPopup } from '../../components/CaptchaPopup/CaptchaPopup';

export interface CaptchaResponse {
    captcha_id: string;
    captcha_url: string;
}

export interface CaptchaRequestData {
    captcha_id: string;
    captcha_code: string;
}

export interface CaptchaWrapperProps {
    captchaUrl: string;
    visible: boolean;
}

const showCaptchaComponent = (props: CaptchaWrapperProps): Promise<string> => {
    const wrapper = document.body.appendChild(document.createElement('div'));

    const cleanup = (): boolean => ReactDOM.unmountComponentAtNode(wrapper);

    return new Promise<string>((resolve): void => {
        const onSubmit = (value: string): void => {
            cleanup();
            resolve(value);
        };

        const element = React.createElement(CaptchaPopup, {
            ...props,
            onSubmit
        });

        ReactDOM.render(element, wrapper);
    });
};

export const getCaptchaValue = (captchaUrl: string): Promise<string> =>
    showCaptchaComponent({
        captchaUrl,
        visible: true
    });

export enum HttpStatusCode {
    tooManyRequests = 429
}

interface AdditionalInputParams {
    login?: string;
    captcha?: CaptchaRequestData;
}

export const extendInput = (
    input: Request | string,
    additionalParams: AdditionalInputParams = {}
): Promise<Request | string> => {
    const { login, captcha } = additionalParams;

    if (typeof input === 'string') {
        return Promise.resolve(updateUrl(input, login, captcha));
    }

    const url = updateUrl(input.url, login, captcha);

    return input.blob().then(
        (body: Blob) =>
            new Request(url, {
                method: input.method,
                headers: input.headers,
                body: body,
                referrer: input.referrer,
                referrerPolicy: input.referrerPolicy,
                mode: input.mode,
                credentials: input.credentials,
                cache: input.cache,
                redirect: input.redirect,
                integrity: input.integrity
            })
    );
};

const updateUrl = (urlWithParams: string, login?: string, captcha?: CaptchaRequestData): string => {
    const [url, params] = urlWithParams.split('?');

    const searchParams = new URLSearchParams(params);

    if (login) {
        searchParams.set('ulogin', login);
    }

    if (captcha) {
        searchParams.set('captcha_id', captcha.captcha_id);
        searchParams.set('captcha_code', captcha.captcha_code);
    }

    return `${url}?${searchParams.toString()}`;
};

let captchaPromise: Promise<void> = Promise.resolve();
let capchaInProgress = false;

const fetcher = (input: Request | string, init?: RequestInit): Promise<Response> => {
    return captchaPromise.then(() =>
        fetch(input, init).then(response => {
            if (response.status !== HttpStatusCode.tooManyRequests) {
                return response;
            }

            if (capchaInProgress) {
                return fetcher(input, init);
            }

            capchaInProgress = true;

            const requestPromise = retryRequestUntilCaptchaSolved(response, input, init);

            captchaPromise = requestPromise.then(noop, noop).then(() => {
                capchaInProgress = false;
            });

            return requestPromise;
        })
    );
};

export const retryRequestUntilCaptchaSolved = async (
    captchaResponse: Response,
    input: Request | string,
    init?: RequestInit
): Promise<Response> => {
    while (captchaResponse.status === HttpStatusCode.tooManyRequests) {
        const responseJson: CaptchaResponse = await captchaResponse.json();

        const captchaCode = await getCaptchaValue(responseJson.captcha_url);

        const captcha = {
            captcha_id: responseJson.captcha_id,
            captcha_code: captchaCode
        };

        const extendedInput = await extendInput(input, {
            captcha
        });

        captchaResponse = await fetch(extendedInput, init);
    }

    return captchaResponse;
};

export const getFetchWrapper = (
    login?: string
): ((input: Request | string, init?: RequestInit) => Promise<Response>) => {
    const fetchWrapper = (input: Request | string, init?: RequestInit): Promise<Response> => {
        return extendInput(input, {
            login
        }).then(extendedInput => fetcher(extendedInput, init));
    };

    return fetchWrapper;
};

export type Fetch = (input?: Request | string, init?: RequestInit) => Promise<Response>;
